import { eventChannel, EventChannel } from "redux-saga";
import {
  call,
  put,
  take,
  fork,
  cancel,
  select,
  delay,
} from "redux-saga/effects";
import { Task } from "redux-saga";
import { SSEService, SSEEventData } from "@/services/sseService";
import { RootState } from "@/store/store";
import {
  connectSSE,
  sseConnected,
  sseDisconnected,
  sseConnectionError,
  disconnectSSE,
  sseEventReceived,
  handleProductUpdate,
  handleCategoryUpdate,
  handleOrderUpdate,
  handleInventoryUpdate,
  handleNotification,
  handleGenericEvent,
  SSEEvent,
} from "@/store/slices/sse.slice";

// Import other slices for cross-slice updates
import { toast } from "sonner";

let sseService: SSEService | null = null;

// Toggle SSE logging here. Set to `true` to re-enable logs for debugging.
const SSE_DEBUG = false;
const sseLog = (...args: any[]) => {
  if (SSE_DEBUG) console.log(...args);
};
const sseError = (...args: any[]) => {
  if (SSE_DEBUG) console.error(...args);
};

// Create event channel for SSE
function createSSEChannel(storeId: string): EventChannel<
  | SSEEventData
  | {
      type: "CONNECTION_ESTABLISHED" | "CONNECTION_ERROR" | "CONNECTION_CLOSED";
      error?: string;
    }
> {
  return eventChannel((emitter) => {
    sseService = new SSEService({
      storeId,
      onEvent: (eventData: SSEEventData) => {
        emitter(eventData);
      },
      onConnect: () => {
        emitter({ type: "CONNECTION_ESTABLISHED", data: null });
      },
      onDisconnect: () => {
        emitter({ type: "CONNECTION_CLOSED", data: null });
      },
      onError: (error: Event) => {
        emitter({
          type: "CONNECTION_ERROR",
          data: null,
          error: error.type || "Connection error",
        });
      },
    });

    sseService.connect();

    // Return cleanup function
    return () => {
      if (sseService) {
        // sseService.disconnect();
        sseService = null;
      }
    };
  });
}

// Handle individual SSE events and dispatch appropriate actions
function* handleSSEEvent(eventData: SSEEventData) {
  const sseEvent: SSEEvent = {
    id: eventData.id,
    type: eventData.type,
    data: eventData.data,
    timestamp: Date.now(),
  };

  // Always dispatch the generic event received action
  yield put(sseEventReceived(sseEvent));

  // Handle specific event types
  switch (eventData.type) {
    // Product events
    case "product.created":
    case "product.updated":
    case "product.deleted":
      yield put(handleProductUpdate(eventData.data));
      // Optionally refresh products list
      // yield put(fetchProductsRequest({ page: 1, pageSize: 10 }));
      break;

    // Category events
    case "category.created":
    case "category.updated":
    case "category.deleted":
      yield put(handleCategoryUpdate(eventData.data));
      // Optionally refresh categories list
      // yield put(fetchCategoriesRequest({ page: 1, pageSize: 10 }));
      break;

    // Order events
    case "order.created":
    case "order.updated":
    case "order.status_changed":
      yield put(handleOrderUpdate(eventData.data));
      // Show toast notification for important order updates
      if (eventData.type === "order.created") {
        toast.success("New order received!");
      }
      break;

    // Inventory events
    case "inventory.updated":
    case "inventory.low_stock":
      yield put(handleInventoryUpdate(eventData.data));
      // Show warning for low stock
      if (eventData.type === "inventory.low_stock") {
        toast.warning(
          `Low stock alert: ${eventData.data.product_name || "Product"}`,
        );
      }
      break;

    // Notification events
    case "notification":
    case "alert":
      yield put(handleNotification(eventData.data));
      // Show toast notification
      if (eventData.data.level === "error") {
        toast.error(eventData.data.message);
      } else if (eventData.data.level === "warning") {
        toast.warning(eventData.data.message);
      } else {
        toast.info(eventData.data.message);
      }
      break;

    // System events
    case "system.maintenance":
    case "system.update":
      yield put(
        handleGenericEvent({ type: eventData.type, data: eventData.data }),
      );
      toast.info(eventData.data.message || "System update");
      break;

    // Heartbeat/ping events (usually silent)
    case "ping":
    case "heartbeat":
      // Don't show notifications for these
      break;

    // Default case for unknown event types
    default:
      yield put(
        handleGenericEvent({ type: eventData.type, data: eventData.data }),
      );
      sseLog(`🔄 Unhandled SSE event type: ${eventData.type}`, eventData.data);
      break;
  }
}

// Main SSE saga that manages the connection
function* sseConnectionSaga() {
  let sseChannel: EventChannel<any> | null = null;
  let watchTask: Task | null = null;

  try {
    while (true) {
      // Wait for connect action
      yield take(connectSSE.type);

      // Get store info to get storeId
      const authState: RootState["auth"] = yield select(
        (state: RootState) => state.auth,
      );
      const storeId = authState.currentStoreInfo?.storeId;

      if (!storeId) {
        yield put(sseConnectionError("No store ID available"));
        continue;
      }

      try {
        // Create SSE channel
        sseChannel = yield call(createSSEChannel, storeId);

        // Fork a task to watch the SSE channel
        watchTask = yield fork(function* () {
          try {
            sseLog("🔄 SSE watch task started");
            while (true) {
              sseLog("🔍 SSE watch task waiting for events...");
              const eventData: SSEEventData = yield take(sseChannel!);
              sseLog("📡 SSE watch task received event:", eventData);

              // Handle connection events
              if (eventData.type === "CONNECTION_ESTABLISHED") {
                sseLog("✅ SSE watch task: Connection established");
                yield put(sseConnected());
                continue;
              }

              if (eventData.type === "CONNECTION_ERROR") {
                const errMsg = (eventData as any).error || "Connection error";
                sseLog("❌ SSE watch task: Connection error", errMsg);
                yield put(sseConnectionError(errMsg));
                continue;
              }

              if (eventData.type === "CONNECTION_CLOSED") {
                sseLog("🔌 SSE watch task: Connection closed");
                yield put(sseDisconnected());
                continue;
              }

              // Handle regular SSE events
              sseLog("🔄 SSE watch task: Handling SSE event");
              yield call(handleSSEEvent, eventData);
            }
          } catch (error) {
            sseError("❌ SSE watch task error:", error);
            yield put(
              sseConnectionError(
                error instanceof Error ? error.message : "Watch task error",
              ),
            );
          }
        });

        // Wait for disconnect action
        yield take(disconnectSSE.type);
      } catch (error) {
        sseError("❌ SSE connection error:", error);
        yield put(
          sseConnectionError(
            error instanceof Error ? error.message : "Connection failed",
          ),
        );
      } finally {
        // Cleanup
        if (watchTask) {
          yield cancel(watchTask);
        }
        if (sseChannel) {
          sseChannel.close();
        }
        // if (sseService) {
        //   sseService.disconnect();
        // }
        yield put(sseDisconnected());
      }
    }
  } catch (error) {
    sseError("❌ SSE saga error:", error);
    yield put(
      sseConnectionError(
        error instanceof Error ? error.message : "SSE saga error",
      ),
    );
  }
}

// Auto-reconnect saga with exponential backoff
function* sseAutoReconnectSaga(): Generator<any, void, any> {
  while (true) {
    // Wait for connection error
    const errorAction = (yield take(sseConnectionError.type)) as {
      payload?: unknown;
    };
    sseLog("🔄 Auto-reconnect triggered by error:", errorAction.payload);

    // Get current state
    const sseState: RootState["sse"] = yield select(
      (state: RootState) => state.sse,
    );
    sseLog("🔄 SSE State on error:", sseState);

    // Check if we should retry
    if (sseState.connectionRetries < sseState.maxRetries) {
      // Exponential backoff delay
      const delay_ms = Math.min(
        1000 * Math.pow(2, sseState.connectionRetries),
        30000,
      );
      sseLog(
        `🔄 Auto-reconnecting SSE in ${delay_ms}ms (attempt ${sseState.connectionRetries + 1})`,
      );

      yield delay(delay_ms);

      // Only reconnect if still needed
      const currentState: RootState["sse"] = yield select(
        (state: RootState) => state.sse,
      );
      if (!currentState.connected && !currentState.connecting) {
        sseLog("🔄 Proceeding with auto-reconnect");
        yield put(connectSSE());
      } else {
        sseLog("🔄 Skipping auto-reconnect - already connected or connecting");
      }
    } else {
      sseLog("❌ SSE max reconnection attempts reached");
      toast.error("Connection lost. Please refresh the page.");
    }
  }
}

// Root SSE saga
export default function* sseRootSaga() {
  yield fork(sseConnectionSaga);
  yield fork(sseAutoReconnectSaga);
}
